import { Controller } from '@hotwired/stimulus'
import Logger from '../lib/logger'

/**
 * AutoEventController
 *
 * A generic, reusable controller that dispatches a custom event on connect.
 * Designed to replace inline <script> tags in Turbo Stream responses,
 * ensuring CSP compliance while preserving the same behavior.
 *
 * Usage:
 *   <div data-controller="auto-event"
 *        data-auto-event-name-value="toast:show"
 *        data-auto-event-detail-value='{"message":"Saved!","type":"success"}'>
 *   </div>
 *
 *   <!-- Dispatch to a specific element instead of window -->
 *   <div data-controller="auto-event"
 *        data-auto-event-name-value="quote-update:refresh"
 *        data-auto-event-target-value="[data-controller='quote-update']">
 *   </div>
 */
export default class extends Controller {
  static values = {
    name: String,
    detail: { type: String, default: '{}' },
    target: { type: String, default: '' }
  }

  connect () {
    Logger.debug('AutoEventController connected', { controller: this.constructor.name, event: this.nameValue })
    this._dispatch()
    // Self-destruct: remove the element after dispatching to keep DOM clean
    this.element.remove()
  }

  disconnect () {
    Logger.debug('AutoEventController disconnected', { controller: this.constructor.name })
  }

  // -- Private --

  _dispatch () {
    let detail = {}
    try {
      detail = JSON.parse(this.detailValue)
    } catch (e) {
      Logger.warn('AutoEventController: Failed to parse detail JSON', { error: e.message })
    }

    const event = new CustomEvent(this.nameValue, { detail, bubbles: true })

    if (this.targetValue) {
      const targetEl = document.querySelector(this.targetValue)
      if (targetEl) {
        targetEl.dispatchEvent(event)
      } else {
        Logger.warn('AutoEventController: Target element not found', { selector: this.targetValue })
      }
    } else {
      window.dispatchEvent(event)
    }

    Logger.debug('AutoEventController: Dispatched event', { event: this.nameValue, detail })
  }
}
